/**
 * AES-256-GCM decryption for MQTT payloads using WebCrypto SubtleCrypto API.
 *
 * Wire format: [nonce (12 bytes) | ciphertext | auth_tag (16 bytes)]
 */

const keyCache = new Map(); // hexKey -> CryptoKey

async function importKey(hexKey) {
    if (keyCache.has(hexKey)) {
        return keyCache.get(hexKey);
    }
    const keyBytes = new Uint8Array(hexKey.match(/.{2}/g).map(b => parseInt(b, 16)));
    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBytes,
        {name: 'AES-GCM'},
        false,
        ['decrypt']
    );
    keyCache.set(hexKey, cryptoKey);
    return cryptoKey;
}

/**
 * Decrypt an AES-256-GCM encrypted MQTT payload.
 * @param {Uint8Array} binaryPayload - Raw binary: nonce(12) + ciphertext + tag(16)
 * @param {string} hexKey - 64-char hex string (32-byte AES-256 key)
 * @returns {Promise<string>} Decrypted JSON string
 */
export async function decryptMqttPayload(binaryPayload, hexKey) {
    const cryptoKey = await importKey(hexKey);
    const nonce = binaryPayload.slice(0, 12);
    // WebCrypto expects ciphertext + tag concatenated (which is exactly what we have after the nonce)
    const ciphertextAndTag = binaryPayload.slice(12);
    const decrypted = await crypto.subtle.decrypt(
        {name: 'AES-GCM', iv: nonce},
        cryptoKey,
        ciphertextAndTag
    );
    return new TextDecoder().decode(decrypted);
}

export function clearKeyCache() {
    keyCache.clear();
}
